import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Search } from "lucide-react";
import { LogoMark } from "../Logo";
import AdminLogin from "./AdminLogin";
import ChangePasswordDialog from "./ChangePasswordDialog";
import SignupsTable from "./SignupsTable";

const API = "/api";

const fmtDate = (iso) => {
    try {
        const d = new Date(iso);
        return (
            d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
            ", " +
            d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
        );
    } catch {
        return iso || "";
    }
};

function Dashboard({ user, onSignOut }) {
    const [data, setData] = useState({ interest: [], partners: [] });
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (q) => {
        setLoading(true);
        try {
            const { data: d } = await axios.get(`${API}/admin/signups`, {
                params: { search: q || "" },
                withCredentials: true,
            });
            setData({ interest: d.interest || [], partners: d.partners || [] });
        } catch (e) {
            if (e.response?.status === 401 || e.response?.status === 403) onSignOut(true);
            return;
        } finally {
            setLoading(false);
        }
    }, [onSignOut]);

    useEffect(() => {
        const t = setTimeout(() => load(search), search ? 300 : 0);
        return () => clearTimeout(t);
    }, [search, load]);

    const handleDelete = useCallback(async (recordType, recordId) => {
        try {
            await axios.post(`${API}/admin/delete-record`, { record_type: recordType, record_id: recordId }, { withCredentials: true });
            await load(search);
        } catch (e) {
            if (e.response?.status === 401 || e.response?.status === 403) onSignOut(true);
        }
    }, [load, search, onSignOut]);

    const handleResend = useCallback(async (recordType, recordId) => {
        try {
            await axios.post(`${API}/admin/resend-notification`, { record_type: recordType, record_id: recordId }, { withCredentials: true });
            await load(search);
        } catch (e) {
            if (e.response?.status === 401 || e.response?.status === 403) onSignOut(true);
            else throw e;
        }
    }, [load, search, onSignOut]);

    const total = data.interest.length + data.partners.length;
    const emailLink = (email) => (
        <a
            href={`mailto:${email}`}
            className="whitespace-nowrap font-semibold text-teal-700 underline decoration-clay underline-offset-4 hover:text-teal-800"
        >
            {email}
        </a>
    );
    const trunc = (text) => (text && text.length > 60 ? `${text.slice(0, 60)}…` : text || "—");

    return (
        <div className="min-h-screen bg-cream">
            <div className="grain-overlay" aria-hidden="true" />
            <header className="sticky top-0 z-40 border-b border-clay bg-cream/90 backdrop-blur-md">
                <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-2 px-5 sm:gap-4 sm:px-8">
                    <span className="flex shrink-0 items-center gap-2.5">
                        <LogoMark className="h-8 w-8" />
                        <span className="hidden font-display text-lg font-extrabold tracking-tight text-ink sm:inline">Fall Safe Ready</span>
                        <span className="rounded-full bg-brand-amber/20 px-2.5 py-0.5 text-sm font-bold uppercase tracking-wide text-amber-deep">
                            Admin
                        </span>
                    </span>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <span className="hidden text-base font-semibold text-ink-soft sm:block">{user.email}</span>
                        <ChangePasswordDialog />
                        <button
                            type="button"
                            onClick={() => onSignOut(false)}
                            data-testid="admin-signout-button"
                            className="inline-flex h-12 items-center gap-2 whitespace-nowrap rounded-full border-2 border-ink px-4 text-base font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-cream sm:px-5"
                        >
                            <LogOut className="h-4 w-4" aria-hidden="true" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 lg:py-14">
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl" data-testid="admin-dashboard-title">
                    Signups &amp; inquiries
                </h1>
                <p className="mt-2 text-lg text-ink-soft">
                    {total} {total === 1 ? "record" : "records"} so far, newest first. New submissions arrive here
                    instantly.
                </p>
                <div className="mt-8">
                    <div className="relative max-w-xl">
                        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-soft/60" aria-hidden="true" />
                        <input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, email, organization, ZIP…"
                            aria-label="Search signups and inquiries"
                            data-testid="admin-search-input"
                            className="input-field pl-12"
                        />
                    </div>
                </div>
                <div className="mt-8 space-y-10" aria-live="polite">
                    {loading && total === 0 ? (
                        <p className="py-16 text-center text-lg text-ink-soft" data-testid="admin-loading">
                            Loading…
                        </p>
                    ) : (
                        <>
                            <SignupsTable
                                title="Interest-list signups"
                                testid="admin-table-interest"
                                rows={data.interest}
                                fileBase="interest-signups"
                                onDelete={(recordId) => handleDelete("interest", recordId)}
                                onResend={(recordId) => handleResend("interest", recordId)}
                                columns={[
                                    { label: "Date", value: (r) => fmtDate(r.timestamp) },
                                    { label: "First name", value: (r) => r.first_name },
                                    { label: "Email", value: (r) => r.email, render: (r) => emailLink(r.email) },
                                    {
                                        label: "Notified",
                                        value: (r) => (r.owner_notified === false ? "No" : "Yes"),
                                        render: (r) => (
                                            <span
                                                className={`inline-flex items-center gap-1.5 whitespace-nowrap font-semibold ${r.owner_notified === false ? "text-[#b42318]" : "text-teal-700"}`}
                                                title={r.owner_notified === false ? "The email notification could not be sent" : "A notification email was sent to Sarah's inbox"}
                                            >
                                                <span className={`h-2 w-2 rounded-full ${r.owner_notified === false ? "bg-[#b42318]" : "bg-teal-600"}`} aria-hidden="true" />
                                                {r.owner_notified === false ? "No" : "Yes"}
                                            </span>
                                        ),
                                    },
                                    { label: "ZIP code", value: (r) => r.zip_code || "—" },
                                    { label: "Interested for", value: (r) => r.interest_for },
                                    { label: "Language", value: (r) => (r.lang === "es" ? "Spanish" : "English") },
                                ]}
                            />
                            <SignupsTable
                                title="Partner inquiries"
                                testid="admin-table-partners"
                                rows={data.partners}
                                fileBase="partner-inquiries"
                                onDelete={(recordId) => handleDelete("partner", recordId)}
                                onResend={(recordId) => handleResend("partner", recordId)}
                                columns={[
                                    { label: "Date", value: (r) => fmtDate(r.timestamp) },
                                    { label: "Name", value: (r) => r.name },
                                    { label: "Organization", value: (r) => r.organization },
                                    { label: "Role", value: (r) => r.role || "—" },
                                    { label: "Email", value: (r) => r.email, render: (r) => emailLink(r.email) },
                                    {
                                        label: "Notified",
                                        value: (r) => (r.owner_notified === false ? "No" : "Yes"),
                                        render: (r) => (
                                            <span
                                                className={`inline-flex items-center gap-1.5 whitespace-nowrap font-semibold ${r.owner_notified === false ? "text-[#b42318]" : "text-teal-700"}`}
                                                title={r.owner_notified === false ? "The email notification could not be sent" : "A notification email was sent to Sarah's inbox"}
                                            >
                                                <span className={`h-2 w-2 rounded-full ${r.owner_notified === false ? "bg-[#b42318]" : "bg-teal-600"}`} aria-hidden="true" />
                                                {r.owner_notified === false ? "No" : "Yes"}
                                            </span>
                                        ),
                                    },
                                    { label: "Phone", value: (r) => r.phone || "—" },
                                    { label: "Organization type", value: (r) => r.org_type || "—" },
                                    { label: "Message", value: (r) => r.message || "—", render: (r) => <span title={r.message || ""}>{trunc(r.message)}</span> },
                                    { label: "Language", value: (r) => (r.lang === "es" ? "Spanish" : "English") },
                                ]}
                            />
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}

export default function Admin() {
    const [user, setUser] = useState(null); // null = checking, false = signed out

    const checkAuth = useCallback(async () => {
        try {
            const { data: u } = await axios.get(`${API}/admin/me`, { withCredentials: true });
            setUser(u);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const signOut = useCallback(async (silent) => {
        if (!silent) {
            try {
                await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
            } catch {
                // clear locally regardless
            }
        }
        setUser(false);
    }, []);

    if (user === null) {
        return (
            <div className="grid min-h-screen place-items-center bg-cream">
                <p className="text-lg font-semibold text-ink-soft" data-testid="admin-checking">
                    Checking sign-in…
                </p>
            </div>
        );
    }
    if (!user) return <AdminLogin onSignedIn={setUser} />;
    return <Dashboard user={user} onSignOut={signOut} />;
}